/*
 * VisitOne localisation. en-GB is the default and fallback; en-XA is a
 * development-only accented expansion pseudo-locale (not a product language).
 * Interface sentences are complete catalogue values and are never concatenated.
 */
import coreGB from './catalogues/core.en-GB.json'
import coreXA from './catalogues/core.en-XA.json'
import formsGB from './catalogues/forms.en-GB.json'
import formsXA from './catalogues/forms.en-XA.json'
import guestsGB from './catalogues/guests.en-GB.json'
import guestsXA from './catalogues/guests.en-XA.json'
import guestAccessGB from './catalogues/guestAccess.en-GB.json'
import guestAccessXA from './catalogues/guestAccess.en-XA.json'
import landlordsGB from './catalogues/landlords.en-GB.json'
import landlordsXA from './catalogues/landlords.en-XA.json'
import orderTypesGB from './catalogues/order-types.en-GB.json'
import orderTypesXA from './catalogues/order-types.en-XA.json'
import peopleGB from './catalogues/people.en-GB.json'
import peopleXA from './catalogues/people.en-XA.json'
import setupGB from './catalogues/setup.en-GB.json'
import setupXA from './catalogues/setup.en-XA.json'
import todayGB from './catalogues/today.en-GB.json'

export type Locale = 'en-GB' | 'en-XA'

export const LOCALES: { value: Locale; labelKey: string }[] = [
  { value: 'en-GB', labelKey: 'core.preferences.english' },
  { value: 'en-XA', labelKey: 'core.preferences.pseudo' },
]

type Dict = Record<string, unknown>

const catalogues: Record<Locale, Record<string, Dict>> = {
  'en-GB': {
    core: coreGB as Dict,
    forms: formsGB as Dict,
    guests: guestsGB as Dict,
    guestAccess: guestAccessGB as Dict,
    landlords: landlordsGB as Dict,
    orderTypes: orderTypesGB as Dict,
    people: peopleGB as Dict,
    setup: setupGB as Dict,
    today: todayGB as Dict,
  },
  // The Today namespace has no pseudo-locale variant; lookups fall back to en-GB.
  'en-XA': {
    core: coreXA as Dict,
    forms: formsXA as Dict,
    guests: guestsXA as Dict,
    guestAccess: guestAccessXA as Dict,
    landlords: landlordsXA as Dict,
    orderTypes: orderTypesXA as Dict,
    people: peopleXA as Dict,
    setup: setupXA as Dict,
  },
}

function lookup(locale: Locale, path: string): string | undefined {
  const [namespace, ...rest] = path.split('.')
  let node: unknown = catalogues[locale][namespace]
  for (const key of rest) {
    if (node && typeof node === 'object') node = (node as Dict)[key]
    else return undefined
  }
  return typeof node === 'string' ? node : undefined
}

type Values = Record<string, string | number>

const numberFormat = new Intl.NumberFormat('en-GB')

/**
 * Minimal ICU-style formatter covering the catalogue's own message shapes:
 * simple `{name}`, `{n, number}`, and `{count, plural, =0{} one{} other{}}`
 * with `#` substitution. It preserves interpolation and plural selection.
 */
function formatMessage(message: string, values: Values = {}): string {
  let out = ''
  let i = 0
  while (i < message.length) {
    const char = message[i]
    if (char !== '{') {
      out += char
      i += 1
      continue
    }
    const { token, next } = readBalanced(message, i)
    out += renderToken(token, values)
    i = next
  }
  return out
}

function readBalanced(source: string, start: number): { token: string; next: number } {
  let depth = 0
  for (let i = start; i < source.length; i += 1) {
    if (source[i] === '{') depth += 1
    else if (source[i] === '}') {
      depth -= 1
      if (depth === 0) return { token: source.slice(start + 1, i), next: i + 1 }
    }
  }
  return { token: source.slice(start + 1), next: source.length }
}

function renderToken(token: string, values: Values): string {
  const firstComma = token.indexOf(',')
  if (firstComma === -1) {
    const value = values[token.trim()]
    return value === undefined ? '' : String(value)
  }
  const name = token.slice(0, firstComma).trim()
  const rest = token.slice(firstComma + 1).trim()
  const value = values[name]

  if (rest === 'number') {
    return typeof value === 'number' ? numberFormat.format(value) : String(value ?? '')
  }

  if (rest.startsWith('plural')) {
    const body = rest.slice('plural'.length).replace(/^\s*,/, '').trim()
    const options = parsePluralOptions(body)
    const count = typeof value === 'number' ? value : Number(value)
    const key =
      options[`=${count}`] !== undefined
        ? `=${count}`
        : count === 1 && options.one !== undefined
          ? 'one'
          : 'other'
    const chosen = options[key] ?? options.other ?? ''
    return formatMessage(chosen.replace(/#/g, numberFormat.format(count)), values)
  }

  return value === undefined ? '' : String(value)
}

function parsePluralOptions(body: string): Record<string, string> {
  const options: Record<string, string> = {}
  let i = 0
  while (i < body.length) {
    if (body[i] === ' ') {
      i += 1
      continue
    }
    let key = ''
    while (i < body.length && body[i] !== '{' && body[i] !== ' ') {
      key += body[i]
      i += 1
    }
    while (i < body.length && body[i] !== '{') i += 1
    if (body[i] !== '{') break
    const { token, next } = readBalanced(body, i)
    options[key.trim()] = token
    i = next
  }
  return options
}

export function createTranslator(locale: Locale) {
  return function t(path: string, values?: Values): string {
    const message = lookup(locale, path) ?? lookup('en-GB', path)
    if (message === undefined) return path
    return formatMessage(message, values)
  }
}

export type Translator = ReturnType<typeof createTranslator>
